import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { getHelpVideos, HelpVideo } from '../services/api';

interface HelpVideosModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpVideosModal: React.FC<HelpVideosModalProps> = ({ isOpen, onClose }) => {
  const { colors } = useTheme();
  const [videos, setVideos] = useState<HelpVideo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<HelpVideo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedVideo(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getHelpVideos();
        if (cancelled) return;
        setVideos(data);
      } catch (err) {
        console.error('Failed to load help videos:', err);
        if (!cancelled) setError('Failed to load help videos. Please try again later.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: colors.surface,
          borderRadius: '12px',
          width: '100%',
          maxWidth: '960px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
            Help &amp; Tutorials
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              color: colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = colors.buttonHover)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div
            style={{
              width: '280px',
              borderRight: `1px solid ${colors.border}`,
              overflowY: 'auto',
              padding: '12px'
            }}
          >
            {isLoading && (
              <div style={{ padding: '12px', color: colors.textSecondary, fontSize: '14px' }}>
                Loading…
              </div>
            )}
            {error && !isLoading && (
              <div style={{ padding: '12px', color: '#ef4444', fontSize: '14px' }}>{error}</div>
            )}
            {!isLoading && !error && videos.length === 0 && (
              <div style={{ padding: '12px', color: colors.textSecondary, fontSize: '14px' }}>
                No tutorials available yet.
              </div>
            )}
            {videos.map((video) => {
              const isSelected = selectedVideo?.id === video.id;
              return (
                <button
                  key={video.id}
                  onClick={() => setSelectedVideo(video)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    marginBottom: '4px',
                    borderRadius: '8px',
                    backgroundColor: isSelected ? colors.surfaceElevated : 'transparent',
                    border: `1px solid ${isSelected ? colors.border : 'transparent'}`,
                    cursor: 'pointer',
                    color: colors.textPrimary,
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = colors.buttonHover;
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span>{video.title}</span>
                  {video.description && (
                    <span style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 400 }}>
                      {video.description}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {selectedVideo ? (
              <>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>
                  {selectedVideo.title}
                </h3>
                {selectedVideo.description && (
                  <p style={{ margin: '6px 0 16px', color: colors.textSecondary, fontSize: '14px' }}>
                    {selectedVideo.description}
                  </p>
                )}
                <video
                  key={selectedVideo.id}
                  src={selectedVideo.url}
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    backgroundColor: '#000',
                    maxHeight: '60vh'
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </>
            ) : (
              !isLoading && (
                <div style={{ margin: 'auto', color: colors.textSecondary, fontSize: '14px' }}>
                  Select a tutorial to start watching.
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpVideosModal;
